import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";

import {
  Building2,
  CheckCircle2Icon,
  Loader2Icon,
  Search,
  Users,
  XCircleIcon,
} from "lucide-react";
import LOGO from "assets/logo.svg";
import LOGO_WHITE from "assets/logo-white.svg";
import { toast } from "utils/toast";

import { verifyAccount } from "../../../api/accounts";

import { Button } from "js/components/ui/button";

const features = [
  {
    icon: Building2,
    title: "Gestión de Propiedades",
    description: "Administra todas tus propiedades en un solo lugar",
  },
  {
    icon: Search,
    title: "Investigaciones",
    description: "Verifica a tus inquilinos con reportes completos",
  },
  {
    icon: Users,
    title: "Gestión de Inquilinos",
    description: "Mantén un registro organizado de todos tus inquilinos",
  },
];

type VerificationStatus = "loading" | "success" | "error" | "already_verified";

const VerifyAccountAnonymous = () => {
  const navigate = useNavigate();
  const { verificationToken } = useParams();
  const [status, setStatus] = useState<VerificationStatus>("loading");

  const handleVerification = async (token: string) => {
    try {
      await verifyAccount(token);
      setStatus("success");
    } catch (error: any) {
      const message = error?.response?.data?.message;
      if (message === "Account already verified") {
        setStatus("already_verified");
      } else {
        setStatus("error");
        toast.error(message || "Error al verificar la cuenta");
      }
    }
  };

  useEffect(() => {
    if (verificationToken) {
      handleVerification(verificationToken);
    }
  }, [verificationToken]);

  const handleContinue = () => {
    navigate("/login");
  };

  const getStatusContent = () => {
    switch (status) {
      case "loading":
        return {
          icon: (
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
              <Loader2Icon className="h-10 w-10 animate-spin text-primary" />
            </div>
          ),
          title: "Verificando tu cuenta",
          titleColor: "text-foreground",
          description: "Por favor espera mientras validamos tu cuenta...",
        };
      case "success":
        return {
          icon: (
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2Icon className="h-10 w-10 text-green-600" />
            </div>
          ),
          title: "Cuenta verificada",
          titleColor: "text-green-600",
          description:
            "Tu cuenta ha sido verificada exitosamente. Ya puedes acceder a todas las funcionalidades de la plataforma.",
        };
      case "already_verified":
        return {
          icon: (
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
              <CheckCircle2Icon className="h-10 w-10 text-primary" />
            </div>
          ),
          title: "Cuenta ya verificada",
          titleColor: "text-primary",
          description:
            "Esta cuenta ya ha sido verificada anteriormente. Puedes iniciar sesión normalmente.",
        };
      case "error":
        return {
          icon: (
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
              <XCircleIcon className="h-10 w-10 text-red-600" />
            </div>
          ),
          title: "Error de verificación",
          titleColor: "text-red-600",
          description:
            "No pudimos verificar tu cuenta. El enlace puede haber expirado o ser inválido.",
        };
    }
  };

  const statusContent = getStatusContent();

  return (
    <div className="flex min-h-screen">
      {/* Left Section - Branding */}
      <div
        className="relative hidden w-1/2 overflow-hidden lg:flex"
        style={{
          background:
            "linear-gradient(135deg, #0f0c4a 0%, #161467 50%, #1e1a7a 100%)",
        }}
      >
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient mesh */}
          <div
            className="absolute -top-40 -left-40 h-[700px] w-[700px] rounded-full opacity-50 blur-[100px]"
            style={{
              background:
                "radial-gradient(circle, rgba(124,58,237,0.6) 0%, transparent 70%)",
            }}
          />
          <div
            className="absolute -bottom-40 -right-40 h-[600px] w-[600px] rounded-full opacity-40 blur-[100px]"
            style={{
              background:
                "radial-gradient(circle, rgba(79,70,229,0.6) 0%, transparent 70%)",
            }}
          />

          {/* Top right pattern - Isometric blueprint grid */}
          <svg
            className="absolute -top-6 -right-6 h-[320px] w-[320px] opacity-[0.08]"
            viewBox="0 0 320 320"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Isometric grid lines - going down-left */}
            <line x1="320" y1="0" x2="160" y2="92" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="40" x2="120" y2="155" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="80" x2="80" y2="218" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="120" x2="40" y2="282" stroke="white" strokeWidth="0.5" />
            <line x1="280" y1="0" x2="0" y2="161" stroke="white" strokeWidth="0.5" />
            <line x1="240" y1="0" x2="0" y2="138" stroke="white" strokeWidth="0.5" />
            <line x1="200" y1="0" x2="0" y2="115" stroke="white" strokeWidth="0.5" />

            {/* Isometric grid lines - going down-right */}
            <line x1="320" y1="0" x2="200" y2="69" stroke="white" strokeWidth="0.5" />
            <line x1="280" y1="0" x2="120" y2="92" stroke="white" strokeWidth="0.5" />
            <line x1="240" y1="0" x2="40" y2="115" stroke="white" strokeWidth="0.5" />
            <line x1="200" y1="0" x2="0" y2="115" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="60" x2="160" y2="152" stroke="white" strokeWidth="0.5" />
            <line x1="320" y1="120" x2="200" y2="189" stroke="white" strokeWidth="0.5" />

            {/* Isometric building/property shapes */}
            <path d="M260 40 L300 63 L300 103 L260 80 Z" stroke="white" strokeWidth="0.75" fill="none" />
            <path d="M260 40 L220 63 L220 103 L260 80 Z" stroke="white" strokeWidth="0.75" fill="none" />
            <path d="M220 63 L260 40 L300 63 L260 86 Z" stroke="white" strokeWidth="0.75" fill="none" />

            <path d="M180 90 L210 106 L210 136 L180 120 Z" stroke="white" strokeWidth="0.5" fill="none" />
            <path d="M180 90 L150 106 L150 136 L180 120 Z" stroke="white" strokeWidth="0.5" fill="none" />
            <path d="M150 106 L180 90 L210 106 L180 122 Z" stroke="white" strokeWidth="0.5" fill="none" />

            <rect x="240" y="140" width="50" height="35" stroke="white" strokeWidth="0.5" fill="none" transform="skewY(-30) skewX(0)" />
            <line x1="265" y1="110" x2="265" y2="135" stroke="white" strokeWidth="0.5" strokeDasharray="2 2" />

            <circle cx="300" cy="63" r="2" fill="white" fillOpacity="0.4" />
            <circle cx="260" cy="40" r="2" fill="white" fillOpacity="0.4" />
            <circle cx="220" cy="63" r="2" fill="white" fillOpacity="0.3" />
          </svg>

          {/* Bottom left pattern - Isometric blueprint grid */}
          <svg
            className="absolute -bottom-6 -left-6 h-[280px] w-[280px] opacity-[0.08]"
            viewBox="0 0 280 280"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <line x1="0" y1="280" x2="140" y2="199" stroke="white" strokeWidth="0.5" />
            <line x1="0" y1="240" x2="160" y2="148" stroke="white" strokeWidth="0.5" />
            <line x1="0" y1="200" x2="180" y2="96" stroke="white" strokeWidth="0.5" />
            <line x1="40" y1="280" x2="200" y2="188" stroke="white" strokeWidth="0.5" />
            <line x1="80" y1="280" x2="240" y2="188" stroke="white" strokeWidth="0.5" />

            <line x1="0" y1="280" x2="100" y2="222" stroke="white" strokeWidth="0.5" />
            <line x1="0" y1="240" x2="80" y2="194" stroke="white" strokeWidth="0.5" />
            <line x1="0" y1="200" x2="60" y2="165" stroke="white" strokeWidth="0.5" />

            <path d="M40 240 L80 218 L120 240 L120 270 L80 292 L40 270 Z" stroke="white" strokeWidth="0.75" fill="none" />
            <line x1="80" y1="218" x2="80" y2="248" stroke="white" strokeWidth="0.5" />

            <line x1="60" y1="229" x2="60" y2="255" stroke="white" strokeWidth="0.5" strokeDasharray="3 2" />
            <line x1="100" y1="229" x2="100" y2="255" stroke="white" strokeWidth="0.5" strokeDasharray="3 2" />

            <path d="M150 220 L175 206 L175 236 L150 250 Z" stroke="white" strokeWidth="0.5" fill="none" />
            <path d="M150 220 L125 206 L125 236 L150 250 Z" stroke="white" strokeWidth="0.5" fill="none" />

            <circle cx="80" cy="218" r="2" fill="white" fillOpacity="0.4" />
            <circle cx="40" cy="240" r="1.5" fill="white" fillOpacity="0.3" />
            <circle cx="120" cy="240" r="1.5" fill="white" fillOpacity="0.3" />
          </svg>

          {/* Subtle noise texture */}
          <div
            className="absolute inset-0 opacity-[0.25]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              mixBlendMode: "overlay",
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 flex w-full flex-col items-center justify-between p-8 text-white xl:p-12">
          {/* Logo */}
          <a
            href="https://multiburo.com.mx/"
            className="absolute left-8 top-8 xl:left-12 xl:top-12"
          >
            <img
              src={LOGO_WHITE}
              alt="Multiburo"
              className="h-14 w-auto xl:h-16"
            />
          </a>

          {/* Center Content - Welcome Message */}
          <div className="flex flex-1 flex-col items-center justify-center">
            <motion.div
              className="max-w-md text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="mb-3 text-3xl font-bold leading-tight tracking-tight xl:text-4xl">
                ¡Bienvenido a Multiburo!
              </h2>
              <p className="mb-8 text-lg text-white/70">
                Ya puedes aprovechar todas nuestras herramientas para
                simplificar tu gestión inmobiliaria.
              </p>

              {/* Feature Cards */}
              <div className="flex flex-col gap-3">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <motion.div
                      key={index}
                      className="flex items-center gap-4 rounded-xl p-4"
                      style={{
                        background: "rgba(255, 255, 255, 0.06)",
                        backdropFilter: "blur(12px)",
                        border: "1px solid rgba(255, 255, 255, 0.08)",
                      }}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.2 + index * 0.1 }}
                    >
                      <div
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                        style={{ background: "rgba(255, 255, 255, 0.1)" }}
                      >
                        <Icon className="h-5 w-5 text-white/80" />
                      </div>
                      <div className="text-left">
                        <h3 className="text-sm font-semibold text-white">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-white/50">
                          {feature.description}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>

          {/* Footer */}
          <div className="text-center text-sm text-white/40">
            {new Date().getFullYear()} Todos los derechos reservados
          </div>
        </div>
      </div>

      {/* Right Section - Form */}
      <div className="relative flex flex-1 flex-col justify-center px-6 py-12 sm:px-12 lg:px-16">
        <div className="mx-auto w-full max-w-md space-y-8">
          {/* Logo - visible only on mobile */}
          <motion.div
            className="flex justify-start lg:hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <a href="https://multiburo.com.mx/">
              <img src={LOGO} alt="Multiburo" className="h-14 w-auto" />
            </a>
          </motion.div>

          {/* Status Icon */}
          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            {statusContent.icon}
          </motion.div>

          {/* Header */}
          <motion.div
            className="space-y-2 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <h1
              className={`text-2xl font-bold tracking-tight sm:text-3xl ${statusContent.titleColor}`}
            >
              {statusContent.title}
            </h1>
            <p className="text-muted-foreground">{statusContent.description}</p>
          </motion.div>

          {/* Actions */}
          {status !== "loading" && (
            <motion.div
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <Button
                onClick={handleContinue}
                className="h-11 w-full text-base font-semibold"
              >
                {status === "error"
                  ? "Ir al inicio de sesión"
                  : "Continuar al inicio de sesión"}
              </Button>

              {status === "error" && (
                <div className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">
                  <p>
                    Si continúas teniendo problemas, intenta solicitar un nuevo
                    enlace de verificación o contacta a soporte.
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyAccountAnonymous;
